import React, { useEffect, useState } from 'react';

import { useQuery } from 'react-query';
import { DotBricksLoading, ListPagination } from 'src/common/components';
import { useDebouncedValue } from 'src/common/hooks';

import { fetchUsers } from '../api';
import { CitasUser, Paginated } from '../types';
import { formatDate, formatDateTime } from '../utils';

const userLabel = (u: CitasUser): string => u.name || u.email || u.phone || 'Unnamed user';

export const Users: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput);

  useEffect(() => setPage(1), [search]);

  const { data, isLoading, isError } = useQuery<{ data: Paginated<CitasUser> }>(
    ['citasUsers', page, search],
    () => fetchUsers({ page, search }),
    { keepPreviousData: true }
  );

  const users = data?.data?.results ?? [];
  const totalCount = data?.data?.count ?? 0;

  return (
    <div className="sm:px-6 lg:px-8 flex flex-col h-full pt-8 pb-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Users</h2>
        <input
          type="search"
          placeholder="Search name, email, phone…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-2.0 w-64"
        />
      </div>

      {isLoading ? (
        <DotBricksLoading />
      ) : isError ? (
        <p className="text-red-600">Failed to load users. Please try again.</p>
      ) : users.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <p className="text-base font-medium">No users found</p>
          <p className="mt-1 text-sm">Patients who sign up on citas.pr will show up here.</p>
        </div>
      ) : (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-auto border border-gray-200 rounded shadow-sm">
            <table className="min-w-full text-left">
              <thead className="bg-gray-50 sticky top-0 text-xs tracking-wider text-gray-500 uppercase">
                <tr>
                  <th className="px-4 py-2">User</th>
                  <th className="px-4 py-2 text-right">Appts requested</th>
                  <th className="px-4 py-2">User since</th>
                  <th className="px-4 py-2">Last session</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 border-t border-gray-200">
                    <td className="px-4 py-2 text-sm">
                      <span className="block text-gray-900">{userLabel(user)}</span>
                      {(user.email || user.phone) && user.name && (
                        <span className="text-xs text-gray-400">{user.email || user.phone}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-right text-gray-700">
                      {user.appointments_requested}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {formatDateTime(user.last_session_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ListPagination totalCount={totalCount} currentPage={page} setCurrentPage={setPage} />
        </div>
      )}
    </div>
  );
};
